import React, { useEffect, useMemo } from 'react';
import { Select, InputNumber } from '@bedrock/components';
import cx from 'classnames';
import AmountOrder from '#components/AmountOrder';
import { useControllableValue, useMemoizedFn } from 'ahooks';
import { currencyEnum, financial } from '#utils/number';
import { objectTransenum } from './utils';
import './CurrencyType.less';
import { localPx2rem, stylePx2Rem } from '#utils';
import { isBlank } from '@bedrock/utils';
import { CurrencyEnum } from '#services/api';
import { useChangeRate } from '#hooks/useChangeRate';
import Decimal from 'decimal.js';

// 统一判断值是否为 NaN 或其字符串形式（大小写不敏感），避免误伤合法数值字符串（如 "100,000"）
const isNaNLike = (value: unknown): boolean =>
  typeof value === 'number'
    ? Number.isNaN(value)
    : typeof value === 'string' && value.toLowerCase() === 'nan';

type Props = {
  showAmountOrder?: boolean;
  inputStyle?: React.CSSProperties;
  selectStyle?: React.CSSProperties;
  view?: boolean;
  disabled?: boolean;
  selectDisabled?: boolean;
  inputDisabled?: boolean;
  onChange?: (value) => void;
  value?: {
    value: string | number;
    currency: string;
  };
  showCurrency?: boolean;
  currencyColor?: 'yellow' | 'black' | 'red' | 'green' | 'grey';
  wrapperProps?: React.HTMLAttributes<HTMLDivElement>;
  textStyle?: React.CSSProperties;
  currencyStyle?: React.CSSProperties;
  /** 展示币种，只在readonly模式下有效 */
  viewCurrencyType?: CurrencyEnum;
  extra?: React.ReactNode;
} & Omit<React.ComponentProps<typeof InputNumber>, 'value'>;

const CurrencyType: React.FC<Props> = (props: Props) => {
  const {
    view,
    disabled,
    selectDisabled,
    selectStyle,
    inputDisabled,
    inputStyle,
    value,
    onChange,
    onBlur: externalOnBlur,
    wrapperProps = {},
    showCurrency = true,
    currencyColor = 'yellow',
    showAmountOrder = true,
    textStyle,
    currencyStyle,
    viewCurrencyType,
    extra,
    ...otherProps
  } = props;
  const [state, setState] = useControllableValue(props, {
    defaultValue: {
      value: '',
      currency: '',
    } as {
      value: string | number;
      currency: string;
    },
  });
  const currencyOptions = objectTransenum(currencyEnum);
  const { changeRateList } = useChangeRate();

  const exchangeRate = useMemo(() => {
    if (viewCurrencyType && state.currency && view) {
      if (state.currency.toLowerCase() === viewCurrencyType.toLowerCase()) {
        return 1;
      }
      const rate = changeRateList.find(
        (item) =>
          item.from_currency?.toLowerCase() === state.currency.toLowerCase() &&
          item.to_currency?.toLowerCase() === viewCurrencyType.toLowerCase()
      )?.exchange_rate;
      if (isBlank(rate)) {
        console.warn(`汇率不存在: ${state.currency} -> ${viewCurrencyType}`);
      }
      return rate;
    }
    return 1;
  }, [viewCurrencyType, state, view, changeRateList]);

  const handleInputChange = useMemoizedFn((value) => {
    // InputNumber 失焦时会把 formatter 后的千分位显示值（如 "100,000"）再触发一次
    // onChange，无法 parse 时会传 null/undefined，此时应忽略，避免已输入的金额被清空。
    if (value === null || value === undefined) return;
    // 清空输入时 InputNumber 的 parser 对空值返回 Number.NaN（配合 toFixed/formatter
    // 可能传出字符串 "NaN"），若原样存入会把 NaN 泄漏进表单值，导致输入框显示 "NaN"、
    // 或提交时金额为 NaN 且必填校验放行。统一归一化为空字符串，保持「清空 = 空值」语义。
    const normalizedValue = isNaNLike(value) ? '' : value;
    const newState = {
      ...state,
      value: normalizedValue,
    };
    setState(newState);
  });

  const handleInputBlurs = useMemoizedFn((e) => {
    // blur 时 e.target.value 是 formatter 处理后的千分位字符串（如 "100,000"），
    // 直接写回会污染 value 类型，这里去掉逗号还原为数字（空值保持 ''）。
    const cleaned = `${e.target.value ?? ''}`.replace(/,/g, '');
    const inputValue = cleaned === '' ? '' : Number(cleaned);
    // 显示值可能是 "NaN"（NaN 经 toFixed/formatter 输出的字符串），同样归一化为空字符串，
    // 避免 NaN 回写表单。
    const normalizedValue = isNaNLike(inputValue) ? '' : inputValue;
    const newState = {
      ...state,
      value: normalizedValue,
    };
    setState(newState);
  });

  const handleSelectChange = useMemoizedFn((currency) => {
    const newState = {
      ...state,
      currency,
    };
    setState(newState);
  });

  const { className, ...restWrapperProps } = wrapperProps;

  const rateChangeAvailable = useMemo(() => {
    return !isBlank(exchangeRate) && !isNaN(Number(state?.value));
  }, [exchangeRate, state?.value]);

  return (
    <div
      className={cx(
        'sform-item-currency-type',
        {
          'wrapper-view': view,
        },
        currencyColor,
        className
      )}
      {...restWrapperProps}
    >
      <div
        className={cx('currency-type-left', {
          view: view,
        })}
      >
        {view ? (
          <div className={cx('currency-type-text')} style={textStyle}>
            {state?.value || state?.value === 0 ? (
              <span>
                {financial(
                  rateChangeAvailable
                    ? new Decimal(state?.value).mul(exchangeRate).toNumber()
                    : state?.value
                )}
              </span>
            ) : (
              '--'
            )}
          </div>
        ) : (
          <InputNumber
            style={{ ...inputStyle }}
            max={Number.MAX_SAFE_INTEGER - 1}
            min={0.01}
            value={state?.value || ''}
            direction="vertical"
            onChange={handleInputChange}
            disabled={inputDisabled || disabled}
            toFixed={2}
            onBlur={(e) => {
              // 外部 onBlur（如充值金额的 refreshBatchCalc）不应覆盖内部 blur 归一化：
              // 先执行内部 handleInputBlurs（去逗号/NaN 归一化 + onChange），再回调外部 onBlur。
              handleInputBlurs(e);
              externalOnBlur?.(e);
            }}
            autoComplete="off"
            formatter={(value) => {
              // InputNumber 在展示受控 value 时会内部先过一遍 parser 再交给
              // formatter（parser 对空值/非法值可能产出 NaN），若 formatter 不做
              // 防护，`${NaN}` 结果是字符串 "NaN"（千分位正则匹配不到连续 3 位数字，
              // 原样返回），导致输入框在清空后残留显示 "NaN"。这里在格式化前统一
              // 拦截 NaN/'NaN'/null/undefined/空值，直接展示为空字符串。
              if (
                value === null ||
                value === undefined ||
                value === '' ||
                isNaNLike(value)
              ) {
                return '';
              }
              return `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
            }}
            // 与 formatter 对应的反向解析：失焦时 InputNumber 内部会用千分位显示值
            // （如 "50,000.00"）再解析一次，去掉逗号才能正确还原数字并正常显示；
            // 否则解析失败会 setVal(null) 导致金额显示被清空。空值不再返回 NaN
            // （避免 NaN 在 InputNumber 内部往返传播、泄漏进展示层），改为返回
            // 空字符串占位，交由外部的清空语义统一处理。
            // ⚠️ 不能 Number() 强转：InputNumber 输入过程 handleInput→parser 会把
            // "1." 强转成 1，绕过内部 String(newValue).endsWith(".") 保护分支，导致
            // 金额无法输入小数。返回去逗号后的字符串即可，非法字符/NaN 由
            // calcInputNumber 过滤为空串，与 formatter 的 NaN 防护共同兜底。
            parser={(value) => {
              const cleaned = String(value ?? '').replace(/,/g, '');
              if (cleaned === '') return '' as unknown as number;
              return cleaned as unknown as number;
            }}
            {...otherProps}
          />
        )}
        {showAmountOrder && (
          <AmountOrder
            style={{ marginTop: localPx2rem(view ? 0 : 4) }}
            value={
              view && rateChangeAvailable
                ? new Decimal(state?.value || 0).mul(exchangeRate).toNumber()
                : state?.value
            }
          />
        )}
      </div>
      {!!showCurrency && (
        <div
          className={cx('currency-type-right', {
            view: view,
          })}
        >
          {view ? (
            <div className={cx('currency-type-text')} style={currencyStyle}>
              {!isBlank(state?.value) && state?.currency ? (
                <span className={cx('currency-type-value')}>
                  {
                    currencyEnum[
                      viewCurrencyType && rateChangeAvailable
                        ? viewCurrencyType.toLowerCase()
                        : state?.currency?.toLocaleLowerCase?.()
                    ]
                  }
                </span>
              ) : (
                '--'
              )}
            </div>
          ) : (
            <Select
              style={{ ...stylePx2Rem(selectStyle) }}
              value={state?.currency?.toLocaleLowerCase()}
              onChange={handleSelectChange}
              options={currencyOptions}
              disabled={selectDisabled || disabled}
              {...otherProps}
              zIndex={10000}
            />
          )}
        </div>
      )}
      {extra && <div className="currency-type-extra">{extra}</div>}
    </div>
  );
};

export default CurrencyType;
